// Live state for the intelligence map. Generates a "frozen now" snapshot
// at demo start: where every gate stands (empty / arriving / parked /
// pushing), what aircraft is at it, and where the AM-shift drivers are
// scattered across the apron.
//
// This isn't simulated forward in time — it's just a believable peak-bank
// state that the rest of the board can react to. Things like alerts
// reference IDs from here so cross-linking lights up the same entities
// across panels.

import { ROSTER } from '../mockRoster';
import { BANK } from '../mockBank';
import { GATE_POSITIONS } from '../../flight-data/gatePositions';

// SVG coordinate system the map uses. Concourse is roughly 1600 × 800.
export const MAP_W = 1600;
export const MAP_H = 800;

// Convert a gate's GATE_POSITIONS entry into an (x, y) in our SVG.
// West wing on left, East wing on right; North side on top, South on bottom.
// Each wing gets ~45% of the SVG width with a thin terminal in the middle.
const WEST_LEFT  = 60;
const WEST_RIGHT = 720;
const EAST_LEFT  = 880;
const EAST_RIGHT = 1540;
const NORTH_Y    = 200;
const SOUTH_Y    = 600;

export function gateXY(gateNum) {
  const pos = GATE_POSITIONS[gateNum];
  if (!pos) return null;
  const wing = pos.wing;
  const left  = wing === 'west' ? WEST_LEFT  : EAST_LEFT;
  const right = wing === 'west' ? WEST_RIGHT : EAST_RIGHT;
  const pct   = parseFloat(pos.left); // "45.5%" → 45.5
  const x     = left + (right - left) * (pct / 100);
  const y     = pos.side === 'north' ? NORTH_Y : SOUTH_Y;
  return { x, y, side: pos.side, wing };
}

// Build a list of all gates we render.
export const GATES = Object.keys(GATE_POSITIONS).map(numStr => {
  const gateNum = parseInt(numStr, 10);
  const xy = gateXY(gateNum);
  return {
    id: `C${gateNum}`,
    num: gateNum,
    x: xy.x,
    y: xy.y,
    side: xy.side,    // 'north' or 'south'
    wing: xy.wing,    // 'west' or 'east'
  };
});

// Deterministic RNG so the demo is identical every load.
function makeRng(seed) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6D2B79F5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const rng = makeRng(2026);
function pick(arr) { return arr[Math.floor(rng() * arr.length)]; }
function rand(lo, hi) { return Math.floor(rng() * (hi - lo + 1)) + lo; }

// Assign aircraft to roughly half the gates (mix of arriving/parked/pushing).
// Aircraft body extends from the gate outward (away from terminal centerline).
const STATES = ['empty', 'arriving', 'parked', 'parked', 'parked', 'pushing'];
const TAIL_PREFIX = 'N';

export const GATE_STATE = (() => {
  const map = new Map();
  const inboundFlights = [...BANK]; // 14 inbounds
  const usedFlights = new Set();

  GATES.forEach(g => {
    const state = pick(STATES);
    if (state === 'empty') {
      map.set(g.id, { state: 'empty' });
      return;
    }
    // Pull a flight number from BANK if we have one unused, else mock.
    let flight = inboundFlights.find(f => !usedFlights.has(f.id));
    if (flight) usedFlights.add(flight.id);
    const flightNumber = flight ? flight.flightNumber : `WN ${rand(100, 4900)}`;
    const origin       = flight ? flight.from : pick(['MCO', 'LAS', 'PHX', 'BNA']);

    map.set(g.id, {
      state,
      flightNumber,
      origin,
      tail: `${TAIL_PREFIX}${rand(100, 999)}WN`,
      eta: flight ? flight.eta : null,
    });
  });
  return map;
})();

// Place AM drivers at random apron positions. Some are "in run" (transit
// between gates), some are at a gate (parked at carousel/jet bridge),
// some are on break/lunch (clustered near break room areas).
const STATUS_DISTRIBUTION = [
  { code: 'on_shift', weight: 35 },  // available, idle
  { code: 'in_run',   weight: 40 },  // active run, transit
  { code: 'at_gate',  weight: 15 },  // working at a specific gate
  { code: 'on_break', weight: 6 },
  { code: 'on_lunch', weight: 4 },
];

function pickStatus() {
  const total = STATUS_DISTRIBUTION.reduce((s, x) => s + x.weight, 0);
  let r = rng() * total;
  for (const s of STATUS_DISTRIBUTION) {
    r -= s.weight;
    if (r <= 0) return s.code;
  }
  return 'on_shift';
}

// Apron areas (in the ConcourseMap world coordinate system, 2400×1200).
// Concourse band is roughly y=380..720 (gates row top..bottom). Apron
// extends above and below; break room sits in the terminal interior.
const BREAK_ROOM  = { x: 1200, y: 540, r: 80 };
const APRON_NORTH = { x1: 120, x2: 2280, y1: 200, y2: 360 };
const APRON_SOUTH = { x1: 120, x2: 2280, y1: 740, y2: 980 };

export const DRIVERS = ROSTER.AM.map((d, i) => {
  const status = pickStatus();
  let x, y, gateId = null;

  if (status === 'on_break' || status === 'on_lunch') {
    // Cluster around break room
    const angle = rng() * Math.PI * 2;
    const r = rng() * BREAK_ROOM.r;
    x = BREAK_ROOM.x + Math.cos(angle) * r;
    y = BREAK_ROOM.y + Math.sin(angle) * r;
  } else if (status === 'at_gate') {
    // Park them at a specific gate (between gate and apron)
    const g = GATES[Math.floor(rng() * GATES.length)];
    gateId = g.id;
    const apronOffset = g.side === 'north' ? 35 : -35;
    x = g.x + (rng() - 0.5) * 12;
    y = g.y + apronOffset;
  } else {
    // On shift / in run — scatter across apron
    const apron = rng() < 0.5 ? APRON_NORTH : APRON_SOUTH;
    x = apron.x1 + rng() * (apron.x2 - apron.x1);
    y = apron.y1 + rng() * (apron.y2 - apron.y1);
  }

  // Driver heading (degrees) — for "in_run" we'll show a direction arrow.
  const heading = status === 'in_run' ? Math.floor(rng() * 360) : null;

  return {
    id: d.id,
    badge: d.badge,
    name: d.displayName,
    seniority: d.seniority,
    status,
    x, y, heading,
    gateId,
    // Sometimes assign a "current run" target gate so we can draw route lines.
    targetGateId: status === 'in_run'
      ? GATES[Math.floor(rng() * GATES.length)].id
      : null,
  };
});

// Quick lookup helpers
export const DRIVER_BY_ID = new Map(DRIVERS.map(d => [d.id, d]));
export const GATE_BY_ID   = new Map(GATES.map(g => [g.id, g]));

// Aggregate counts for the top status bar
export function liveCounts() {
  const out = {
    drivers: DRIVERS.length,
    onShift: 0, inRun: 0, atGate: 0, onBreak: 0, onLunch: 0,
    gatesParked: 0, gatesArriving: 0, gatesPushing: 0, gatesEmpty: 0,
  };
  DRIVERS.forEach(d => {
    if (d.status === 'on_shift') out.onShift++;
    if (d.status === 'in_run')   out.inRun++;
    if (d.status === 'at_gate')  out.atGate++;
    if (d.status === 'on_break') out.onBreak++;
    if (d.status === 'on_lunch') out.onLunch++;
  });
  GATE_STATE.forEach(s => {
    if (s.state === 'parked')   out.gatesParked++;
    if (s.state === 'arriving') out.gatesArriving++;
    if (s.state === 'pushing')  out.gatesPushing++;
    if (s.state === 'empty')    out.gatesEmpty++;
  });
  return out;
}

// Useful when alerts need to scroll the map to a specific entity.
export function entityXY(entity) {
  if (!entity) return null;
  if (entity.kind === 'gate') {
    const g = GATE_BY_ID.get(entity.id);
    return g ? { x: g.x, y: g.y } : null;
  }
  if (entity.kind === 'driver') {
    const d = DRIVER_BY_ID.get(entity.id);
    return d ? { x: d.x, y: d.y } : null;
  }
  if (entity.kind === 'flight') {
    // Find the gate hosting this flight
    for (const [gid, s] of GATE_STATE.entries()) {
      if (s.flightNumber === entity.id) {
        const g = GATE_BY_ID.get(gid);
        return g ? { x: g.x, y: g.y } : null;
      }
    }
  }
  return null;
}
